// Rasterizes a string to a CanvasTexture. Cached by (text + style) key so repeated tiles
// (identical $ values) reuse the same GPU texture.

import { CanvasTexture, LinearFilter, SRGBColorSpace, type Texture } from 'three';

export interface TextStyle {
  font: string;
  color: string;
  bg?: string;
  width: number;
  height: number;
  /** Center-aligned by default. */
  align?: 'center' | 'left' | 'right';
  paddingX?: number;
  weight?: 'normal' | 'bold';
  lineHeight?: number;
  stroke?: string;
  strokeWidth?: number;
  /**
   * Rounded-corner radius (px at the base 1920×1080 stage). When set and `bg`
   * is also set, the filled background is drawn as a rounded rect instead of
   * a plain rect. Pixels outside the rounded rect are transparent, so the
   * mesh shows through as a rounded card against the scene background.
   */
  borderRadius?: number;
  /**
   * Drop-shadow color (e.g. 'rgba(0,0,0,0.35)'). Only drawn when both
   * `shadowColor` and `shadowBlur` are set. Applied to the bg rounded-rect
   * fill, not the text — text gets a subtle own-shadow via `textShadow` if
   * you want (left off by default for perf).
   */
  shadowColor?: string;
  /** Gaussian blur radius for the bg drop shadow. */
  shadowBlur?: number;
  /** Y offset for the bg drop shadow (positive = down). Default 6. */
  shadowOffsetY?: number;
  /**
   * Auto-shrink the font until the text fits within `width - paddingX*2`.
   * The starting font size is whatever appears in `font`. Defaults to true.
   */
  autoFit?: boolean;
  /** Minimum font size when autoFit is on. Default 12. */
  minFontPx?: number;
}

const cache = new Map<string, Texture>();

export function textTexture(text: string, style: TextStyle): Texture {
  const key = JSON.stringify([text, style]);
  const cached = cache.get(key);
  if (cached) return cached;

  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const canvas = document.createElement('canvas');
  canvas.width = Math.ceil(style.width * dpr);
  canvas.height = Math.ceil(style.height * dpr);
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2D context unavailable');
  ctx.scale(dpr, dpr);

  const radius = style.borderRadius ?? 0;

  // Draw background (rounded or plain) with optional drop shadow. Shadow is
  // applied via canvas-native shadow props on the bg fill only, so the text
  // above stays crisp.
  if (style.bg) {
    if (style.shadowColor && (style.shadowBlur ?? 0) > 0) {
      ctx.save();
      ctx.shadowColor = style.shadowColor;
      ctx.shadowBlur = style.shadowBlur ?? 0;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = style.shadowOffsetY ?? 6;
      ctx.fillStyle = style.bg;
      if (radius > 0) {
        // Inset the rounded rect so the drop shadow has room to render
        // without getting clipped at the canvas edge.
        const inset = Math.max(2, (style.shadowBlur ?? 0) / 2);
        drawRoundedRect(ctx, {
          x: inset,
          y: inset,
          w: style.width - inset * 2,
          h: style.height - inset * 2,
          r: radius,
        });
        ctx.fill();
      } else {
        ctx.fillRect(0, 0, style.width, style.height);
      }
      ctx.restore();
    } else {
      ctx.fillStyle = style.bg;
      if (radius > 0) {
        drawRoundedRect(ctx, { x: 0, y: 0, w: style.width, h: style.height, r: radius });
        ctx.fill();
      } else {
        ctx.fillRect(0, 0, style.width, style.height);
      }
    }
  }

  const weight = style.weight ?? 'bold';
  const padding = style.paddingX ?? 16;
  const maxTextW = style.width - padding * 2;

  // Auto-shrink: re-measure until it fits the width. Uses the font-size
  // extracted from `style.font`, then decrements until the longest line
  // fits. Only applies when autoFit is true (default) and text would
  // otherwise overflow — keeps the "designed" size when possible.
  const baseFontPx = parseFontSize(style.font);
  const fontFamily = style.font.replace(/^\s*\d+px\s*/, '');
  const autoFit = style.autoFit ?? true;
  const minFontPx = style.minFontPx ?? 12;
  let fontPx = baseFontPx;
  if (autoFit) {
    ctx.font = `${weight} ${fontPx}px ${fontFamily}`;
    // Find widest required width if we used the whole string on one line.
    // wrapLines will handle the actual line breaks, but autoFit protects the
    // case where a single long word (e.g. "GEOGRAPHY") won't break.
    while (fontPx > minFontPx) {
      ctx.font = `${weight} ${fontPx}px ${fontFamily}`;
      const widest = longestWordWidth(ctx, text);
      if (widest <= maxTextW) break;
      fontPx -= 2;
    }
  }
  ctx.font = `${weight} ${fontPx}px ${fontFamily}`;

  ctx.fillStyle = style.color;
  ctx.textBaseline = 'middle';
  const align = style.align ?? 'center';
  ctx.textAlign = align;
  const x = align === 'center' ? style.width / 2 : align === 'left' ? padding : style.width - padding;

  const lines = wrapLines(ctx, text, maxTextW);
  const lineH = style.lineHeight ?? fontPx * 1.1;
  const blockH = lines.length * lineH;
  const startY = style.height / 2 - blockH / 2 + lineH / 2;
  lines.forEach((ln, i) => {
    const y = startY + i * lineH;
    if (style.stroke && style.strokeWidth) {
      ctx.lineWidth = style.strokeWidth;
      ctx.strokeStyle = style.stroke;
      ctx.strokeText(ln, x, y);
    }
    ctx.fillText(ln, x, y);
  });

  const tex = new CanvasTexture(canvas);
  tex.colorSpace = SRGBColorSpace;
  tex.minFilter = LinearFilter;
  tex.magFilter = LinearFilter;
  tex.needsUpdate = true;
  cache.set(key, tex);
  return tex;
}

interface RoundedRect {
  x: number;
  y: number;
  w: number;
  h: number;
  r: number;
}

/** Draws a rounded-rect path (no fill/stroke — caller chooses). */
function drawRoundedRect(ctx: CanvasRenderingContext2D, rect: RoundedRect): void {
  const { x, y, w, h, r } = rect;
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.lineTo(x + w - rr, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + rr);
  ctx.lineTo(x + w, y + h - rr);
  ctx.quadraticCurveTo(x + w, y + h, x + w - rr, y + h);
  ctx.lineTo(x + rr, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - rr);
  ctx.lineTo(x, y + rr);
  ctx.quadraticCurveTo(x, y, x + rr, y);
  ctx.closePath();
}

/** Width of the longest contiguous word (no whitespace break available). */
function longestWordWidth(ctx: CanvasRenderingContext2D, text: string): number {
  let max = 0;
  for (const word of text.split(/\s+/)) {
    const w = ctx.measureText(word).width;
    if (w > max) max = w;
  }
  return max;
}

function wrapLines(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const paragraphs = text.split('\n');
  const out: string[] = [];
  for (const para of paragraphs) {
    const words = para.split(/\s+/);
    let current = '';
    for (const w of words) {
      const candidate = current ? `${current} ${w}` : w;
      if (ctx.measureText(candidate).width <= maxWidth) {
        current = candidate;
      } else {
        if (current) out.push(current);
        current = w;
      }
    }
    if (current) out.push(current);
  }
  return out;
}

function parseFontSize(font: string): number {
  const m = font.match(/(\d+)px/);
  return m?.[1] ? Number(m[1]) : 24;
}

export function clearTextTextureCache(): void {
  for (const t of cache.values()) t.dispose();
  cache.clear();
}
